import React, { FormEvent, useState } from 'react';
import { aiSqlService } from '../../services/aiSqlService';
import { AiSqlResult } from '../../types/AiSql';
import AiSqlChart from '../../components/reports/AiSqlChart';

const DEFAULT_PROMPT =
  'Show total invoice revenue by month for 2024 for customers Bloom Foods and Koenig.';

const SqlExplorationPage: React.FC = () => {
  const [question, setQuestion] = useState(DEFAULT_PROMPT);
  const [result, setResult] = useState<AiSqlResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!question.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await aiSqlService.runQuestion(question.trim());
      setResult(response);
      console.groupCollapsed('[AI SQL] Generated SQL');
      console.log('Preview SQL:\n', response.previewSql);
      console.log('Export SQL:\n', response.exportSql);
      if (response.summarySql) {
        console.log('Summary SQL:\n', response.summarySql);
      }
      console.groupEnd();
    } catch (err: any) {
      setError(err.message || 'Unable to run AI SQL query.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!result) return;
    setExporting(true);
    setError(null);

    try {
      const blob = await aiSqlService.exportCsv(result.requestId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-sql-export-${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to export CSV.');
    } finally {
      setExporting(false);
    }
  };

  const renderTable = () => {
    if (!result) return null;

    if (result.rows.length === 0) {
      return <p className="ai-sql__empty">No rows returned for this query.</p>;
    }

    return (
      <div className="ai-sql__table-wrapper">
        <table className="ai-sql__table">
          <thead>
            <tr>
              {result.columns.map((column) => (
                <th key={column.name}>{column.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row, rowIndex) => (
              <tr key={`${rowIndex}`}>
                {result.columns.map((column) => (
                  <td key={`${rowIndex}-${column.name}`}>{formatCellValue(row[column.name])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSummary = () => {
    if (!result?.summary || result.summary.rows.length === 0) return null;

    const summary = result.summary;
    return (
      <div className="ai-sql__summary">
        <h3>Summary</h3>
        <table>
          <thead>
            <tr>
              {summary.columns.map((column) => (
                <th key={column.name}>{column.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {summary.rows.map((row, index) => (
              <tr key={`summary-${index}`}>
                {summary.columns.map((column) => (
                  <td key={`${index}-${column.name}`}>{formatCellValue(row[column.name])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="page-container ai-sql-page">
      <div className="page-header">
        <h1 className="page-title">🤖 AI SQL Exploration</h1>
        <p className="page-subtitle">
          Ask natural-language questions about invoices. The LLM returns read-only SQL, and we run
          it securely with a read-only database user.
        </p>
      </div>

      <form className="ai-sql__form" onSubmit={handleSubmit}>
        <label htmlFor="question">What do you want to know?</label>
        <textarea
          id="question"
          value={question}
          rows={3}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="e.g. Show total invoice amount by month for 2024 for customer Bloom Foods."
        />
        <div className="ai-sql__actions">
          <button type="submit" disabled={loading}>
            {loading ? 'Running...' : 'Generate SQL'}
          </button>
          {result && (
            <button type="button" className="secondary" onClick={handleExport} disabled={exporting}>
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          )}
        </div>
      </form>

      {error && <div className="ai-sql__error">{error}</div>}

      {result && (
        <div className="ai-sql__results">
          {result.analysis && (
            <section className="ai-sql__analysis">
              <header>
                <h2>Analysis Approach</h2>
              </header>
              <div className="ai-sql__analysis-content">{result.analysis}</div>
            </section>
          )}

          <section>
            <header>
              <h2>Preview (first 20 rows)</h2>
              <p>
                Request ID: <code>{result.requestId}</code>
              </p>
            </header>
            {renderTable()}
          </section>

          <section>
            <header>
              <h2>Visualization</h2>
            </header>
            <AiSqlChart chart={result.chart} rows={result.rows} />
          </section>

          {renderSummary()}

          <section className="ai-sql__sql-block">
            <h3>Generated SQL</h3>
            <details open>
              <summary>Preview SQL</summary>
              <pre>{result.previewSql}</pre>
            </details>
            <details>
              <summary>Export SQL</summary>
              <pre>{result.exportSql}</pre>
            </details>
            {result.summarySql && (
              <details>
                <summary>Summary SQL</summary>
                <pre>{result.summarySql}</pre>
              </details>
            )}
          </section>
        </div>
      )}

      <style>{`
        .ai-sql-page textarea {
          width: 100%;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 1rem;
          font-size: 1rem;
          resize: vertical;
        }

        .ai-sql__form label {
          display: block;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .ai-sql__actions {
          display: flex;
          gap: 1rem;
          margin-top: 1rem;
        }

        .ai-sql__actions button {
          padding: 0.75rem 1.5rem;
          border-radius: 999px;
          border: none;
          background: #2563eb;
          color: white;
          cursor: pointer;
          font-weight: 600;
        }

        .ai-sql__actions button.secondary {
          background: #0f172a;
        }

        .ai-sql__actions button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .ai-sql__results {
          margin-top: 2rem;
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .ai-sql__table-wrapper {
          overflow-x: auto;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
        }

        .ai-sql__table {
          width: 100%;
          border-collapse: collapse;
        }

        .ai-sql__table th,
        .ai-sql__table td {
          padding: 0.75rem;
          border-bottom: 1px solid #f3f4f6;
          text-align: left;
        }

        .ai-sql__table th {
          background: #f9fafb;
          font-weight: 600;
        }

        .ai-sql__error {
          margin-top: 1rem;
          padding: 1rem;
          border-radius: 8px;
          background: #fee2e2;
          color: #b91c1c;
        }

        .ai-sql__analysis {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 8px;
          padding: 1.5rem;
        }

        .ai-sql__analysis header h2 {
          margin-top: 0;
          color: #0369a1;
        }

        .ai-sql__analysis-content {
          color: #0c4a6e;
          line-height: 1.6;
          white-space: pre-wrap;
        }

        .ai-sql__empty {
          padding: 1rem;
          color: #6b7280;
        }

        .ai-sql__summary table {
          width: 100%;
          border-collapse: collapse;
        }

        .ai-sql__summary th,
        .ai-sql__summary td {
          border-bottom: 1px solid #e5e7eb;
          padding: 0.5rem;
          text-align: left;
        }

        .ai-sql__sql-block pre {
          background: #0f172a;
          color: #f8fafc;
          padding: 1rem;
          border-radius: 8px;
          overflow-x: auto;
          font-size: 0.9rem;
        }

        .ai-chart {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1rem;
          background: #fff;
        }

        .ai-chart h3 {
          margin-top: 0;
        }

        .ai-chart__bars {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .ai-chart__bar-row {
          display: grid;
          grid-template-columns: 1fr 3fr auto;
          gap: 0.75rem;
          align-items: center;
        }

        .ai-chart__bar-track {
          background: #e5e7eb;
          border-radius: 999px;
          overflow: hidden;
          height: 10px;
        }

        .ai-chart__bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #2563eb, #7c3aed);
        }

        .ai-chart__bar-value {
          font-weight: 600;
        }

        .ai-chart__line-labels {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          margin-top: 0.5rem;
          color: #6b7280;
        }

        .ai-chart__pie {
          width: 200px;
          height: 200px;
          border-radius: 50%;
          margin: 0 auto;
          position: relative;
        }

        .ai-chart__legend {
          list-style: none;
          padding: 0;
          margin-top: 1rem;
        }

        .ai-chart__legend li {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .ai-chart__legend-swatch {
          width: 16px;
          height: 16px;
          border-radius: 4px;
          display: inline-block;
        }

        details {
          margin-bottom: 0.5rem;
        }

        details summary {
          font-weight: 600;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

function formatCellValue(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value);
}

export default SqlExplorationPage;
